'use client'
import { AuthenticationLink } from "@/components/AuthenticationLink"
import { Mask } from "@/components/Mask"
import { useShowPricing } from "@/components/Pricing"
import { ScrollEvents, usePreventDefault } from "@/hooks/usePreventDefault"
import { usePathname } from "@/locales/routing"
import { useDevice } from "@/provider/DecideProvider"
import { useResponsiveSize } from "@/provider/useResponsiveSize"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { useUserStore } from "@/store/useUserStore"
import { useShallow } from "zustand/react/shallow"
import { runWhenQuiet } from "@/util/dom"
import { AlignLeft } from "@/ui/icons/AlignLeft"
import { ChevronsLeft } from "@/ui/icons/ChevronsLeft"
import { Energy } from "@/ui/icons/Energy"
import { MembershipBadge } from "@/components/MembershipBadge"
import { Luxury } from "@/ui/icons/Luxury"
import { CreateMenuButton } from "./CreateMenuButton"
import { Panel } from "@/ui/Panel/Panel"
import { Button, cn, Image, Link, ScrollShadow } from "@heroui/react"
import { isServer } from "@tanstack/react-query"
import { motion, Variant } from 'framer-motion'
import { useTranslations } from "next-intl"
import NextImage from 'next/image'
import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isLikeChatLayout } from "./Layout"
import { Navigator } from './Navigator'
import { UserAction } from "./UserAction"
import { useRootLayout } from "./useRootLayout"


export const textVariants = {
  close: {
    opacity: 0,
    transition: {
      duration: .001,
      delay: .1
    }
  },
  hidden: {
    opacity: 1,
    transition: {
      duration: .001,
      delay: .1
    }
  },
  default: {
    opacity: 1,
    transition: {
      duration: .001,
      delay: .1
    }
  }
}

export const buttonVariants = {
  close: {
    width: 40,
    borderRadius: 8,
  },
  hidden: {
    width: 240,
    borderRadius: 40,
  },
  default: {
    width: 240,
    borderRadius: 40,
  }
}

export const asideVariants: {
  [key in
  'default' |
  'close']: Variant } = {
  default: {
    width: 281,
    paddingLeft: 20,
    paddingRight: 20,
    transition: {
      ease: 'linear'
    }
  },
  close: {
    width: 57,
    paddingLeft: 8,
    paddingRight: 8,
    transition: {
      ease: 'linear'
    }
  },
}


export const SideBar = memo(({ className = "", children }: PropsWithChildren & { className?: string, defaultOpen?: boolean }) => {
  const size = useResponsiveSize(['default', 'lg'])
  const [aside, setAside, asideWrapper, setAsideWrapper] = useRootLayout(state => [state.aside, state.setAside, state.asideWrapper, state.setAsideWrapper])
  const [isVip, coin, vip_type, star_coin, ad_free] = useUserDetailStore(useShallow(state => [state.vip, state.coin, state.vip_type, state.star_coin, state.ad_free]))
  const device = useDevice()
  const pathname = usePathname()
  const t = useTranslations()
  const showPricing = useShowPricing()

  const isLogined = useUserStore(state => state.isLogined)



  useEffect(() => {
    if (pathname.includes('chats')) {
      setAside('close')
      if (size === 'default') {
        setAsideWrapper('hidden')
      }
    } else if (pathname.includes('search') || size === 'default') {
      setAsideWrapper('hidden')
      setAside('default')
    } else {
      if (device === 'pc') {

        if (asideWrapper !== 'default') {
          setAsideWrapper('default')
        }
      }
      else setAsideWrapper('hidden')
    }
  }, [pathname, size])


  const closeAside = useCallback(() => {
    if (size === 'default') {
      setAside('default')
      setAsideWrapper(asideWrapper === 'default' ? 'hidden' : 'default')
    } else {
      setAside(aside === 'close' ? 'default' : 'close')
    }
  }, [size, aside, asideWrapper])


  const initAnimate = useMemo(() => {
    if (device === 'mobile') {
      return 'hidden'
    } else {
      return isLikeChatLayout(pathname) ? 'close' :
        pathname.includes('search') ?
          'hidden' :
          'default'
    }
  }, [])

  const initWrapperAnimate = useMemo(() => {
    if (device === 'mobile' || pathname.includes('search')) {
      return 'hidden'
    } else {
      return 'default'
    }
  }, [])


  // mask 
  const mask = useRef<HTMLDivElement>(null!)
  usePreventDefault({
    ref: mask, events: ScrollEvents
  })


  const showMask = useMemo(() => {
    if (isServer) return false
    return size === 'default' && asideWrapper === 'default'
  }, [size, asideWrapper])

  // 侧栏收起（移动端默认）时仅靠 translateX 移出屏幕，仍落在 Link 视口预取的判定范围内，
  // 会在 hydration 期间预取首页/聊天/生成等路由并下载执行其 JS（含画布、markdown 等大块），
  // 拖慢首屏交互（INP）。收起时关闭预取，展开后恢复默认预取
  const linkPrefetch = asideWrapper === 'hidden' ? false : undefined

  // 移动端 UA 下侧栏默认收起、完全不可见，但内容（HeroUI 按钮/下拉菜单、framer-motion 动画元素等）
  // hydration 成本很高。首屏只输出轻量占位（公司信息等静态文本仍在 HTML 中），
  // 完整内容等页面空闲且用户暂停操作时再渲染；在此之前展开侧栏则在同一次渲染中立即渲染。
  // device 由 UA 决定，服务端与客户端首帧一致，不会 hydration 不匹配
  const [contentReady, setContentReady] = useState(device !== 'mobile')
  useEffect(() => {
    if (contentReady) return
    return runWhenQuiet(() => setContentReady(true))
  }, [])
  // 展开过一次就保持渲染，避免收起动画中途内容被卸载
  useEffect(() => {
    if (asideWrapper === 'default') setContentReady(true)
  }, [asideWrapper])
  const showContent = contentReady || asideWrapper === 'default'

  const companyInfo = (
    <div className="mb-4 text-[9px] text-theme-primary/30 flex flex-col gap-1">
      <p>LANGUAGE POWER MEDIA LIMITED</p>
      <p>ROOM A1-13, FLOOR 3, BLOCK A , YEE LIM INDUSTRIAL CENTRE 2-28 KWAI LOK STREET , KWAI CHUNG , HongKong</p>
      <a href="mailto:taojiang@hualao.com">taojiang@hualao.com</a>
    </div>
  )


  return (
    <>
      {/* mask */}
      <Mask
        showMask={showMask}
        ref={mask}
        onPointerUpCapture={() => setAsideWrapper('hidden')}
      />

      <motion.aside
        className={`z-50 max-w-[min(100vw,_356px)] lg:z-[49] bg-aside-color box-border left-0  overflow-hidden h-[100dvh] flex  fixed  top-0 ${className}`}
        animate={asideWrapper}
        initial={initWrapperAnimate}
        variants={{
          default: {
            x: 0,
            transition: {
              ease: "linear",
              duration: .1
            }
          },
          hidden: {
            x: -356,
            transition: {
              ease: "linear",
              duration: .1
            }
          }
        }}

      >
        {showContent ? <motion.div
          initial={initAnimate}
          animate={aside}
          variants={asideVariants}
          className="flex-col flex pt-4 sm:pt-6 w-[280px] border-r-1 border-r-theme-primary/10">
          {/* expand button */}
          <div className="w-full flex justify-end">
            <Button
              data-aside={aside || initAnimate}
              data-hidden={pathname.includes('chats')}
              isIconOnly
              className={cn(
                "min-w-[40px] hover:!bg-theme-primary/5 bg-transparent data-[aside=default]:bg-theme-primary/5",
                "rounded-[12px] data-[aside=default]:rounded-full",
                "data-[hidden=true]:opacity-0 data-[hidden=true]:pointer-events-none"
              )}
              onPress={closeAside}>
              {aside === 'close' ? <AlignLeft /> : <ChevronsLeft />}
            </Button>
          </div>

          <div className="mt-4">
            <div>
              <Link className="mx-auto block" href="/">
                <Image
                  as={motion.img}
                  className="object-contain"
                  classNames={{
                    wrapper: "rounded-[8px] mx-auto"
                  }}
                  src="/imgs/logo.png"
                  variants={{
                    close: {
                      width: 40,
                      height: 40
                    },
                    hidden: {
                      width: 64,
                      height: 64
                    },
                    default: {
                      width: 64,
                      height: 64
                    }
                  }}
                />
              </Link>


              <motion.div className="mt-4 leading-[1] font-bold text-center text-[24px]"
                variants={textVariants}>
                {process.env.NEXT_PUBLIC_APP_NAME}
              </motion.div>
            </div>

            {/*  */}
            <motion.div variants={{
              close: {
                opacity: 0,
                transition: {
                  duration: .01
                }
              },
              default: {
                opacity: 1
              }
            }}>
              <Button
                onPress={() => showPricing('premium')}
                data-logined={isLogined}
                className={cn("h-[40px] px-[2px] justify-start items-center flex gap-0 bg-aside-luxury-bg",
                  "mt-6 sm:mt-[32px]",
                  "data-[logined=false]:opacity-0"
                )}
                disableAnimation
                disableRipple
                radius="full"
                fullWidth>
                {/* 会员标识：VIP 展示对应档位；非 VIP 且 ad_free 展示 AD-Free；否则置灰 Luxury */}
                <span className={cn(
                  "flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-surface",
                  !isVip && !ad_free && "grayscale opacity-40"
                )}>
                  {isVip || ad_free
                    ? <MembershipBadge vip={isVip} vipType={vip_type} adFree={ad_free} width={20} height={20} />
                    : <Luxury width={20} height={20} />}
                </span>

                <Panel
                  onPointerUp={e => {
                    e.stopPropagation()
                    showPricing('energy')
                  }}
                  onPointerDown={e => e.stopPropagation()}
                  className="flex !bg-transparent items-center justify-center gap-1 flex-1 font-bold text-[14px]">
                  <Energy width={16} height={16} />
                  <span>{coin}</span>
                </Panel>

                <div className="h-[12px] w-[2px] shrink-0 rounded-[4px] bg-theme-primary opacity-10"></div>

                <Panel
                  onPointerUp={e => {
                    e.stopPropagation()
                    showPricing('energy')
                  }}
                  onPointerDown={e => e.stopPropagation()}
                  className="flex !bg-transparent items-center justify-center gap-1 flex-1 font-bold text-[14px]">
                  <NextImage src="/imgs/creationAiCoin.png" width={16} height={16} alt={t('generatePage.crystals')} loading="eager" />
                  <span>{star_coin}</span>
                </Panel>
              </Button>
            </motion.div>
          </div>

          <div className="flex-1 mt-12 sm:mt-[64px] overflow-hidden ">
            <ScrollShadow
              hideScrollBar
              className="h-full pb-4"
              onTouchMoveCapture={(ev) => { ev.stopPropagation() }}
              onTouchStartCapture={(ev) => { ev.stopPropagation() }}
              onTouchEndCapture={(ev) => { ev.stopPropagation() }}
              onWheelCapture={ev => ev.stopPropagation()}
            >

              <motion.div
                className="relative overflow-hidden mt-4"
                variants={buttonVariants}>
                <CreateMenuButton aside={aside || initAnimate} />
              </motion.div>

              <motion.div
                className="relative overflow-hidden mt-4"
                variants={buttonVariants}>
                <Button
                  as={AuthenticationLink}
                  href="/generate"
                  prefetch={linkPrefetch}
                  data-aside={aside || initAnimate}
                  className={cn(
                    "bg-transparent",
                    "create-character-btn relative transition-all flex min-w-[40px] z-[1] justify-start border-[rgb(var(--c-btn-primary))] border-1 text-theme-primary",
                    "rounded-[12px] data-[aside=default]:rounded-full",
                    "px-[8px] data-[aside=default]:pl-[24px] data-[aside=default]:pr-[16px]"
                  )}
                  startContent={<Image src="/imgs/creationAiCoin.png" classNames={{wrapper:'shrink-0'}} className="size-5"/>} fullWidth>
                  <motion.span variants={textVariants}>{t('generatePage.aiCreation')}</motion.span>
                </Button>
              </motion.div>




              <Navigator className="mt-8 sm:mt-[40px]" aside={aside || initAnimate} prefetch={linkPrefetch}></Navigator>
            </ScrollShadow>
          </div>

          {
            isLogined && <div className="mb-2">
              {
                <UserAction
                  className="flex items-end mt-4 flex-shrink-0"
                  avatarClass="h-10 w-10"
                  triggerClass={`bg-transparent h-12 pl-0 !w-full aria-[expanded=true]:bg-gray-bg ${aside === 'default' ? '' : '!bg-transparent'}`}
                />
              }
            </div>
          }
          {aside !== 'close' ? companyInfo : null}


        </motion.div> :
          <div className="flex-col flex justify-end w-[280px] px-5 border-r-1 border-r-theme-primary/10">{companyInfo}</div>
        }

        {pathname.includes('chats') ? children : null}

      </motion.aside >

    </>

  )
})